import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
// Side-effect import: registers <tile-brush-draw-preview> (the scene-gallery
// preview the asset detail page uses). The reviewer preview reuses it, so a
// submitted brush is composed exactly as the game renders it — via the local
// d3-assets renderer, no d3-kit round-trip.
import '../assets/tile-brush-draw-preview.js';

/**
 * Two reviewer previews for a mapmaking-tile-brush phase submission:
 *
 * 1. **Contact sheet** — each named PSD layer rendered as a labeled <img>
 *    in a CSS grid. Pure HTML; no canvas.
 * 2. **Composition** — the submitted brush composed across the standard render
 *    scenes (the same <tile-brush-draw-preview> the asset detail page uses),
 *    exercising every joiner: seam connector, corner, ending, T-junction, 4-way
 *    cross, and 45° diagonals/crossings. Pan/zoom per scene; flip the
 *    straight-seam connector on/off.
 *
 * The seam-connector toggle here is a **transient preview control** — a
 * commission submission has no stored asset yet, so nothing is persisted
 * (unlike the detail page, where the toggle edits the asset's saved policy).
 *
 * @property {Array<{layer_name: string, file_url: string}>} layers
 *   Extracted-layer descriptors from the API.
 */

class TileBrushPreviews extends BaseComponent {
    static properties = {
        layers: { type: Array },
        // Transient straight-seam-connector policy for the preview only. `true`
        // draws the seam connector on straight seams (matching how a submission renders
        // by default); toggling to `false` lets the reviewer see the segmented
        // look. Persists nothing — there's no asset to store it on yet.
        _useSeamConnector: { state: true },
    };

    static styles = css`
        :host {
            display: block;
            margin-top: 1rem;
        }

        h6 {
            font-size: 0.85rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #6c757d;
            margin: 0 0 0.5rem;
        }

        .contact-sheet {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
            gap: 0.75rem;
            margin-bottom: 1.25rem;
        }

        .contact-cell {
            border: 1px solid #dee2e6;
            border-radius: 0.25rem;
            background: #f8f9fa;
            padding: 0.5rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
        }
        .contact-cell img {
            width: 100%;
            aspect-ratio: 1;
            object-fit: contain;
            background:
                repeating-conic-gradient(#fff 0% 25%, #eef 0% 50%) 50% / 16px 16px;
        }
        .contact-cell figcaption {
            font-family: ui-monospace, SFMono-Regular, monospace;
            font-size: 0.7rem;
            color: #495057;
            margin-top: 0.35rem;
            word-break: break-all;
        }

        .opts { display: flex; align-items: center; gap: 0.4rem; margin: 0 0 0.5rem; }
        .opts label {
            font-size: 0.75rem; color: #495057;
            display: inline-flex; align-items: center; gap: 0.3rem; cursor: pointer;
        }
        .opts .muted { color: #868e96; }
    `;

    constructor() {
        super();
        this.layers = [];
        this._useSeamConnector = true;
    }

    render() {
        const layers = this.layers || [];
        if (layers.length === 0) {
            return html`<p class="text-muted small">Layer extraction is queued; previews will appear here once it finishes.</p>`;
        }

        return html`
            <h6>Tile sheet</h6>
            <div class="contact-sheet">
                ${layers.map(
                    (l) => html`
                    <figure class="contact-cell">
                        <img src="${l.file_url}" alt="${l.layer_name}" loading="lazy">
                        <figcaption>${l.layer_name}</figcaption>
                    </figure>
                `,
                )}
            </div>

            <h6>Composition</h6>
            <div class="opts">
                <label title="Overlay the seam connector on straight seams (continuous brushes). Turn off for segmented/block brushes so straights abut as hard edges. Preview only — nothing is saved.">
                    <input type="checkbox" .checked="${this._useSeamConnector}"
                        @change="${(e) => (this._useSeamConnector = e.target.checked)}">
                    Straight-seam connector <span class="muted">(preview only)</span>
                </label>
            </div>
            <tile-brush-draw-preview
                .layers="${layers}"
                .useSeamConnector="${this._useSeamConnector}"></tile-brush-draw-preview>
        `;
    }
}

customElements.define('tile-brush-previews', TileBrushPreviews);
